import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axiosConfig";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import "./Websites.css";

const Websites = () => {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get("sites");
        console.log("Response from API:", response);
        setWebsites(response.data.data || []);
      } catch (err) {
        console.error("Error fetching sites:", err.response || err.message);
        setError("Failed to fetch websites.");
      } finally {
        setLoading(false);
      }
    };
    fetchSites();
  }, []);

  const filteredWebsites = websites.filter((website) => {
    const matchesSearch =
      website.brand_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      website.http_link?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || website.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusVariant = (status) => {
    return status === "active" ? "success" : "secondary";
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this website?")) {
      try {
        await api.delete(`/sites/${id}`);
        setWebsites(websites.filter((website) => website.id !== id));
      } catch (error) {
        console.error("Error deleting website:", error);
        setError("Failed to delete website.");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container fluid className="sj-websites-container">
      {/* Header */}
      <div className="sj-websites-header">
        <div className="sj-header-content">
          <div className="sj-header-text">
            <h1 className="sj-websites-title">Websites</h1>
            <p className="sj-websites-subtitle">Manage all websites in the system</p>
          </div>
          <Button
            className="sj-add-website-btn"
            onClick={() => navigate("/super-admin/websites/create")}
          >
            <i className="fas fa-plus sj-btn-icon"></i>
            Add Website
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="sj-filters-card">
        <Card.Body className="sj-filters-body">
          <Row className="g-3">
            <Col md={6}>
              <InputGroup className="sj-search-input-group">
                <InputGroup.Text className="sj-search-icon">
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search websites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sj-search-input"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                className="sj-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
               
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="sj-websites-count">
                <span className="sj-count-number">{filteredWebsites.length}</span>
                <span className="sj-count-text">websites</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Loading/Error */}
      {loading && (
        <div className="sj-loading-container">
          <Spinner animation="border" className="sj-loading-spinner" />
          <p className="sj-loading-text">Loading websites...</p>
        </div>
      )}
      
      {error && (
        <Alert variant="danger" className="sj-error-alert">
          <i className="fas fa-exclamation-triangle sj-alert-icon"></i>
          {error}
        </Alert>
      )}

      {/* Websites Grid */}
      {!loading && !error && (
        <div className="sj-websites-grid">
          {filteredWebsites.length === 0 ? (
            <Card className="sj-empty-state-card">
              <Card.Body className="sj-empty-state-body">
                <div className="sj-empty-state-content">
                  <i className="fas fa-globe sj-empty-state-icon"></i>
                  <h3 className="sj-empty-state-title">No websites found</h3>
                  <p className="sj-empty-state-text">
                    {websites.length === 0 
                      ? "Get started by creating your first website." 
                      : "Try adjusting your search or filter criteria."}
                  </p>
                  {websites.length === 0 && (
                    <Button 
                      className="sj-add-website-btn"
                      onClick={() => navigate("/super-admin/websites/create")}
                    >
                      <i className="fas fa-plus sj-btn-icon"></i>
                      Create First Website
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredWebsites.map((website) => (
                <Col key={website.id} xl={4} lg={6} md={6}>
                  <Card className="sj-website-card">
                    <Card.Body className="sj-website-card-body">
                      {/* Website Header */}
                      <div className="sj-website-header">
                        <div className="sj-website-icon">
                          <i className="fas fa-globe"></i>
                        </div>
                        <div className="sj-website-info">
                          <h5 className="sj-website-name">{website.brand_name}</h5>
                         
                        </div>
                      </div>

                      {/* Website Details */}
                      <div className="sj-website-details">
                        <div className="sj-website-detail">
                          <i className="fas fa-link sj-detail-icon"></i>
                          <span className="sj-detail-text">{website.http_link || 'No domain'}</span>
                        </div>
                        <div className="sj-website-detail">
                          <i className="fas fa-calendar sj-detail-icon"></i>
                          <span className="sj-detail-text">{formatDate(website.created_at)}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="sj-website-actions">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="sj-action-btn sj-btn-edit"
                          onClick={() => navigate(`/super-admin/websites/edit/${website.id}`)}
                          title="Edit Website"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                       
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          className="sj-action-btn sj-btn-delete"
                          onClick={() => handleDelete(website.id)}
                          title="Delete Website"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          className="sj-action-btn sj-btn-view"
                          title="View Website"
                          onClick={() => window.open(website.http_link, '_blank')}
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
    </Container>
  );
};

export default Websites;